/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Bryan Sheranian <bryan.sheranian@deliciousai.com>
 */

import React from 'react'
import { Formik, FormikProps, FormikValues } from 'formik'
import {
  DefaultStatus,
  TypedFormikConfig,
  TypedFormikHelpers,
  TypedFormikProps,
} from './types'

/**
 * A Formik wrapper component that handles proper typing for status
 */
function TypedFormik<Values extends FormikValues, Status = DefaultStatus>({
  children,
  onSubmit,
  ...props
}: TypedFormikConfig<Values, Status> & {
  children: (formikProps: TypedFormikProps<Values, Status>) => React.ReactNode
}) {
  return (
    <Formik<Values>
      {...props}
      onSubmit={(values, helpers) => {
        const typedHelpers: TypedFormikHelpers<Values, Status> = helpers
        return onSubmit(values, typedHelpers)
      }}
    >
      {(childProps: FormikProps<Values>) => {
        const typedChildProps: TypedFormikProps<Values, Status> = {
          ...childProps,
          status: childProps.status,
        }
        return children(typedChildProps)
      }}
    </Formik>
  )
}

export default TypedFormik
